import {
  listSigningKeys as listSigningKeysFromDatabase,
  registerSigningKey as registerSigningKeyInDatabase,
  revokeSigningKey as revokeSigningKeyInDatabase,
} from '@mesa/core/auth/signing-keys';
import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { assertScope, baseAuthMiddleware } from '../middleware/base-auth';

const orgInputSchema = z.object({ orgSlug: z.string().trim().min(1).max(100) }).strict();
const registerKeyInputSchema = orgInputSchema.extend({ publicKey: z.string().trim().min(1).max(512) }).strict();
const revokeKeyInputSchema = orgInputSchema.extend({ keyId: z.string().trim().min(1).max(128) }).strict();

export const listSigningKeysServerFn = createServerFn({ method: 'GET' })
  .middleware([baseAuthMiddleware])
  .validator((data) => orgInputSchema.parse(data))
  .handler(async ({ context: { org, member } }) => {
    assertScope(member.role, 'read');
    return (await listSigningKeysFromDatabase(org.id)).map(({ id, publicKey, lastUsedAt, createdAt }) => ({
      id,
      publicKey,
      lastUsedAt,
      createdAt,
    }));
  });

export const registerSigningKeyServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .validator((data) => registerKeyInputSchema.parse(data))
  .handler(async ({ data: { publicKey }, context: { org, member, session } }) => {
    assertScope(member.role, 'admin');
    await registerSigningKeyInDatabase({ orgId: org.id, userId: session.user.id, publicKey });
  });

export const revokeSigningKeyServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .validator((data) => revokeKeyInputSchema.parse(data))
  .handler(async ({ data: { keyId }, context: { org, member } }) => {
    assertScope(member.role, 'admin');
    await revokeSigningKeyInDatabase({ orgId: org.id, keyId });
  });

export type SigningKey = Awaited<ReturnType<typeof listSigningKeysServerFn>>[number];
